import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  TextField,
  toast,
} from '@eon/ui';
import { hooks, type OmUser } from '@eon/api-client';

interface ManageRfidDialogProps {
  user: OmUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  value: string;
  description: string;
}

export function ManageRfidDialog({ user, open, onOpenChange }: ManageRfidDialogProps) {
  const { t } = useTranslation();
  const rfid = hooks.useOmUserRfid(user?.userId ?? '');
  const upsert = hooks.useOmUpsertUserRfid();
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { value: '', description: '' },
  });

  useEffect(() => {
    if (open && rfid.data != null) {
      reset({ value: rfid.data, description: '' });
    }
  }, [open, rfid.data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!user) return;
    try {
      await upsert.mutateAsync({
        userId: user.userId,
        value: values.value.trim(),
        description: values.description.trim() || undefined,
      });
      toast.success(t('portal.users.rfidUpdated'));
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.users.rfidTitle')}</DialogTitle>
        </DialogHeader>
        {user ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-muted-foreground text-sm">{user.name ?? user.email}</p>
            <div className="space-y-1.5">
              <Label htmlFor="rfid-value">{t('portal.users.rfidValue')}</Label>
              <Input
                id="rfid-value"
                className="font-mono"
                {...register('value', { required: true })}
                disabled={rfid.isLoading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rfid-desc">{t('portal.users.rfidDescription')}</Label>
              <TextField id="rfid-desc" {...register('description')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={formState.isSubmitting || upsert.isPending || rfid.isLoading}>
                {t('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
